<script setup>
import { ref, computed, onMounted, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import { format } from 'date-fns'; // Import date-fns for formatting
import FormInput from 'v3/components/Form/Input.vue';
// Ciclo 9 — date picker próprio do app (mesma primitiva de CustomSnoozeModal/
// DateRangePicker, tematizada em plugins/_date-picker.scss). O <input
// type="date"> nativo renderiza a máscara no locale do BROWSER (mm/dd/yyyy em
// browsers en-US) e não há atributo para forçar formato; aqui fixamos a
// exibição em DD/MM/YYYY mantendo o contrato interno YYYY-MM-DD (presets,
// quick filters e filtro server-side intocados).
import DatePicker from 'vue-datepicker-next';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { isSet, parseDateOnlyLocal } from 'dashboard/helper/filterHelper';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  filteredResults: {
    type: Object,
    default: () => ({ total: 0, stages: {} }),
  },
  currentPipeline: {
    type: Object,
    default: null,
  },
  size: {
    type: String,
    default: 'medium',
  },
  initialFilters: {
    type: Object,
    default: null,
  },
  scope: {
    type: String,
    default: 'global', // 'global' or 'column'
  },
  columnName: {
    type: String,
    default: '',
  },
  // eslint-disable-next-line vue/no-unused-properties
  columns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['apply', 'close']);
const { t } = useI18n();
const store = useStore();
const { showAlert } = useAlert();

// Ciclo 8 (a11y) — prefixo único por instância para os ids dos campos do
// formulário: o componente monta no tab global E em cada coluna (lazy), então
// ids fixos duplicariam no DOM e quebrariam a associação label↔campo.
const uid = useId();
const fieldId = suffix => `${uid}-${suffix}`;

// Fonte única do formato de filtro — evita perder chaves (ex.: labels) em
// algum dos vários pontos de reset.
const blankFilters = () => ({
  priority: [],
  value: { min: null, max: null },
  agent_id: null,
  date: { start: null, end: null },
  status: null, // 'won', 'lost', 'open', or null for all
  stages: [], // Filter by specific stages (B4 - Advanced Filters)
  sla_exceeded: false, // Fase 2.1 — só cards que excederam SLA
  labels: [], // NOOVI: filtra por labels da conversa vinculada (server-side)
});

const filters = ref(blankFilters());

// Quick filter presets (B4 - Advanced Filters)
const quickFilters = [
  {
    id: 'today',
    label: 'PIPELINE.QUICK_FILTERS.TODAY',
    icon: 'calendar',
    getFilters: () => {
      const today = new Date().toISOString().split('T')[0];
      return { date: { start: today, end: today } };
    },
  },
  {
    id: 'this_week',
    label: 'PIPELINE.QUICK_FILTERS.THIS_WEEK',
    icon: 'calendar-clock',
    getFilters: () => {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        date: {
          start: monday.toISOString().split('T')[0],
          end: sunday.toISOString().split('T')[0],
        },
      };
    },
  },
  {
    id: 'overdue',
    label: 'PIPELINE.QUICK_FILTERS.OVERDUE',
    icon: 'warning',
    getFilters: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        date: { start: null, end: yesterday.toISOString().split('T')[0] },
        status: 'open',
      };
    },
  },
  {
    id: 'high_value',
    label: 'PIPELINE.QUICK_FILTERS.HIGH_VALUE',
    icon: 'tag',
    getFilters: () => ({ value: { min: 10000, max: null } }),
  },
  {
    id: 'urgent',
    label: 'PIPELINE.QUICK_FILTERS.URGENT',
    icon: 'flame',
    getFilters: () => ({ priority: ['urgent', 'high'] }),
  },
];

const activeQuickFilter = ref(null);

// --- Preset State ---
const savedPresets = ref({});
const newPresetName = ref('');
const appliedPresetName = ref('');

// Chave escopada por usuário+conta (mesmo padrão do PipelineHeader) para não
// vazar presets entre usuários/contas no mesmo browser.
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const presetStorageKey = computed(
  () =>
    `u${currentUserId.value}_a${currentAccountId.value}_pipeline_filter_presets`
);

// Function to check if current filters match any preset
const checkAppliedPreset = currentFilters => {
  if (!currentFilters) return '';
  const matchedName = Object.keys(savedPresets.value).find(name => {
    // Deep comparison, ensure structure matches
    const preset = savedPresets.value[name];
    const current = JSON.parse(JSON.stringify(currentFilters)); // Deep copy for safety
    // Normalize empty values for comparison if necessary
    preset.priority = preset.priority || [];
    preset.value = preset.value || {};
    preset.date = preset.date || {};
    current.priority = current.priority || [];
    current.value = current.value || {};
    current.date = current.date || {};

    return JSON.stringify(preset) === JSON.stringify(current);
  });
  return matchedName || '';
};

// --- Load Presets (mount, key change, modal open) ---
const loadPresets = () => {
  const storedPresets = localStorage.getItem(presetStorageKey.value);
  if (storedPresets) {
    try {
      savedPresets.value = JSON.parse(storedPresets);
    } catch (e) {
      savedPresets.value = {};
    }
  } else {
    // No presets under this key — drop any in-memory presets from a previous
    // user/account so they can't be re-saved under the new scoped key.
    savedPresets.value = {};
  }
};

// User/account switch changes the scoped key — reload immediately so presets
// never leak across scopes within a long-lived component instance.
watch(presetStorageKey, loadPresets);

// Watch for the modal becoming visible or initialFilters changing
watch(
  () => [props.show, props.initialFilters],
  ([newShow, newInitialFilters]) => {
    if (newShow) {
      // Re-read presets on open — storage may have changed in another tab or
      // the scoped key may have changed since mount.
      loadPresets();
      // Default filter structure
      const defaultFilters = blankFilters();

      // When modal opens, load the initial state from the column
      if (newInitialFilters) {
        // Deep copy and merge with defaults to ensure all fields exist
        const loadedFilters = JSON.parse(JSON.stringify(newInitialFilters));
        filters.value = { ...defaultFilters, ...loadedFilters };

        // Ensure arrays are initialized (for legacy presets)
        if (!Array.isArray(filters.value.priority)) {
          filters.value.priority = [];
        }
        if (!Array.isArray(filters.value.stages)) {
          filters.value.stages = [];
        }

        // Ensure agent_id is a number if it's not null
        if (
          filters.value.agent_id !== null &&
          filters.value.agent_id !== undefined
        ) {
          filters.value.agent_id = Number(filters.value.agent_id);
        }
      } else {
        // Reset to default if no initial filters provided
        filters.value = defaultFilters;
      }
      // Check if the loaded filters match a preset
      appliedPresetName.value = checkAppliedPreset(filters.value);
    }
  },
  { immediate: true, deep: true }
);

// Watch for changes to agent_id to ensure it's always a number when selected
watch(
  () => filters.value.agent_id,
  newValue => {
    if (newValue !== null && newValue !== undefined) {
      // Convert to number to ensure consistent types
      filters.value.agent_id = Number(newValue);
    }
  }
);

onMounted(() => {
  loadPresets();
  // Guard por uiFlags além do length: N instâncias montadas no mesmo tick
  // viam o store ainda vazio e disparavam N fetches paralelos.
  if (
    store.getters['agents/getAgents'].length === 0 &&
    !store.getters['agents/getUIFlags'].isFetching
  ) {
    store.dispatch('agents/get');
  }
  if (
    store.getters['labels/getLabels'].length === 0 &&
    !store.getters['labels/getUIFlags'].isFetching
  ) {
    store.dispatch('labels/get');
  }
});
// ---------------------

const priorityOptions = computed(() => [
  {
    label: t('PIPELINE.PRIORITY_LABELS.HIGH'),
    value: 'high',
    colors: {
      border: filters.value.priority.includes('high')
        ? 'border-n-ruby-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('high')
        ? 'text-n-ruby-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-ruby-3 border-n-ruby-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.MEDIUM'),
    value: 'medium',
    colors: {
      border: filters.value.priority.includes('medium')
        ? 'border-n-amber-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('medium')
        ? 'text-n-amber-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-amber-3 border-n-amber-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.LOW'),
    value: 'low',
    colors: {
      border: filters.value.priority.includes('low')
        ? 'border-n-blue-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('low')
        ? 'text-n-blue-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-blue-3 border-n-blue-9',
    },
  },
  {
    label: t('PIPELINE.PRIORITY_LABELS.URGENT'),
    value: 'urgent',
    colors: {
      border: filters.value.priority.includes('urgent')
        ? 'border-n-ruby-9'
        : 'border-n-weak',
      text: filters.value.priority.includes('urgent')
        ? 'text-n-ruby-11'
        : 'text-n-slate-11',
      hover: 'hover:bg-n-alpha-1',
      selected: 'bg-n-ruby-3 border-n-ruby-9',
    },
  },
]);

const agents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});

// NOOVI: labels da conta (mesmas etiquetas exibidas nos cards). O filtro envia
// os títulos selecionados; o backend casa contra item_details.conversation.label_list.
const labelOptions = computed(() => store.getters['labels/getLabels'] || []);

const toggleLabel = title => {
  filters.value.labels = filters.value.labels.includes(title)
    ? filters.value.labels.filter(l => l !== title)
    : [...filters.value.labels, title];
};

// Pipeline stages for stage filter (B4 - Advanced Filters)
const availableStages = computed(() => {
  if (!props.currentPipeline?.stages) return [];
  return Object.entries(props.currentPipeline.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name || id,
      color: stage.color || '#64748B',
      position: parseInt(stage.position, 10) || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Apply quick filter (B4 - Advanced Filters)
const applyQuickFilter = quickFilter => {
  const quickFilterValues = quickFilter.getFilters();

  // Reset all filters first
  filters.value = blankFilters();

  // Apply the quick filter values
  Object.assign(filters.value, quickFilterValues);
  activeQuickFilter.value = quickFilter.id;
  appliedPresetName.value = '';
};

// Check if a quick filter is active
const isQuickFilterActive = quickFilterId =>
  activeQuickFilter.value === quickFilterId;

// --- Helper to get Agent Name by ID ---
const getAgentName = agentId => {
  if (!agentId) return '';
  const agent = agents.value.find(a => a.id === agentId);
  return agent ? agent.name : `ID: ${agentId}`;
};

// --- Helper Function for Preset Summary ---
const getPresetSummary = presetFilters => {
  if (!presetFilters) return '';
  const parts = [];

  // Priority
  if (presetFilters.priority?.length) {
    parts.push(
      `${t('PIPELINE.PRESET.SUMMARY.PRIORITY')}: ${presetFilters.priority.join(', ')}`
    );
  }

  // Value
  const { min, max } = presetFilters.value || {};
  if (min !== undefined && min !== null && max !== undefined && max !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: ${min}-${max}`);
  } else if (min !== undefined && min !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: >= ${min}`);
  } else if (max !== undefined && max !== null) {
    parts.push(`${t('PIPELINE.PRESET.SUMMARY.VALUE')}: <= ${max}`);
  }

  // Agent
  if (presetFilters.agent_id) {
    parts.push(
      `${t('PIPELINE.PRESET.SUMMARY.AGENT')}: ${getAgentName(presetFilters.agent_id)}`
    );
  }

  // Date
  const { start, end } = presetFilters.date || {};
  if (start && end) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: ${format(parseDateOnlyLocal(start), 'dd/MM')} - ${format(parseDateOnlyLocal(end), 'dd/MM')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  } else if (start) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: >= ${format(parseDateOnlyLocal(start), 'dd/MM/yy')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  } else if (end) {
    try {
      parts.push(
        `${t('PIPELINE.PRESET.SUMMARY.DATE')}: <= ${format(parseDateOnlyLocal(end), 'dd/MM/yy')}`
      );
    } catch {
      /* Handle invalid date strings */
    }
  }

  return parts.join('. ') || t('PIPELINE.NO_FILTER_ACTIVE'); // Return summary or default text
};
// ---------------------------------------

const getStageColor = stageName => {
  if (!props.currentPipeline?.stages) return '#64748B';

  const stage = Object.values(props.currentPipeline.stages).find(
    s => s.name === stageName
  );

  if (!stage) {
    const stageById = Object.values(props.currentPipeline.stages).find(
      s => s.id === stageName
    );
    return stageById?.color || '#64748B';
  }

  return stage.color || '#64748B';
};

const hasActiveFilters = computed(() => {
  const hasFilters = Boolean(
    filters.value.labels.length > 0 ||
      filters.value.priority.length > 0 ||
      filters.value.stages.length > 0 ||
      isSet(filters.value.value.min) ||
      isSet(filters.value.value.max) ||
      filters.value.agent_id ||
      filters.value.date.start ||
      filters.value.date.end ||
      filters.value.status ||
      filters.value.sla_exceeded
  );

  return hasFilters && props.filteredResults.total > 0;
});

const handleApply = () => {
  // Ensure agent_id is a number before sending
  const filtersToApply = JSON.parse(JSON.stringify(filters.value));
  if (
    filtersToApply.agent_id !== null &&
    filtersToApply.agent_id !== undefined
  ) {
    filtersToApply.agent_id = Number(filtersToApply.agent_id);
  }

  // Emit the current filters state (deep copy)
  emit('apply', filtersToApply);
  emit('close');
};

const handleClear = () => {
  const clearedFilters = blankFilters();
  filters.value = JSON.parse(JSON.stringify(clearedFilters));
  appliedPresetName.value = '';
  // Emit the cleared state so the column can remove it from localStorage
  emit('apply', JSON.parse(JSON.stringify(clearedFilters)));
  emit('close');
};

// --- Preset Functions ---
const overwriteDialogRef = ref(null);
const deleteDialogRef = ref(null);
const pendingPresetName = ref('');

const persistPreset = name => {
  // Ensure we save with consistent types
  const filtersToSave = JSON.parse(JSON.stringify(filters.value));
  if (filtersToSave.agent_id !== null && filtersToSave.agent_id !== undefined) {
    filtersToSave.agent_id = Number(filtersToSave.agent_id);
  }

  savedPresets.value[name] = filtersToSave;
  localStorage.setItem(
    presetStorageKey.value,
    JSON.stringify(savedPresets.value)
  );
  newPresetName.value = '';
  appliedPresetName.value = name; // Mark the new preset as applied locally
  showAlert(t('PIPELINE.PRESET.SAVE_SUCCESS', { name }));
};

const saveCurrentPreset = () => {
  const name = newPresetName.value.trim();
  if (!name) {
    showAlert(t('PIPELINE.PRESET.ALERT_ENTER_NAME'));
    return;
  }
  if (savedPresets.value[name]) {
    pendingPresetName.value = name;
    overwriteDialogRef.value?.open();
    return;
  }
  persistPreset(name);
};

const confirmOverwritePreset = () => {
  persistPreset(pendingPresetName.value);
  pendingPresetName.value = '';
  overwriteDialogRef.value?.close();
};

const applyPreset = name => {
  if (savedPresets.value[name]) {
    const loadedPreset = JSON.parse(JSON.stringify(savedPresets.value[name]));
    // Merge with defaults to ensure all fields exist (for legacy presets)
    filters.value = { ...blankFilters(), ...loadedPreset };
    // Ensure arrays are properly initialized
    if (!Array.isArray(filters.value.priority)) {
      filters.value.priority = [];
    }
    if (!Array.isArray(filters.value.stages)) {
      filters.value.stages = [];
    }
    appliedPresetName.value = name;
    // User still needs to click "Apply" to confirm
  }
  // Preset not found - no action needed
};

const deletePreset = name => {
  if (!savedPresets.value[name]) return; // Preset not found - no action needed
  pendingPresetName.value = name;
  deleteDialogRef.value?.open();
};

const confirmDeletePreset = () => {
  const name = pendingPresetName.value;
  if (savedPresets.value[name]) {
    // Check if the preset being deleted is the currently applied one
    const deletingApplied = appliedPresetName.value === name;

    delete savedPresets.value[name];
    localStorage.setItem(
      presetStorageKey.value,
      JSON.stringify(savedPresets.value)
    );

    // If the deleted preset was the one applied, clear the applied name —
    // filters remain until user action.
    if (deletingApplied) {
      appliedPresetName.value = '';
    }
    showAlert(t('PIPELINE.PRESET.DELETE_SUCCESS', { name }));
  }
  pendingPresetName.value = '';
  deleteDialogRef.value?.close();
};

const getPriorityColor = priority => {
  const colors = {
    urgent: '#ef4444',
    high: '#f97316',
    medium: '#eab308',
    low: '#64748b',
  };
  return colors[priority] || '#94a3b8';
};
</script>

<template>
  <Teleport to="body">
    <Modal
      v-if="show"
      :show="show"
      :size="props.size"
      :show-close-button="false"
      @close="$emit('close')"
    >
      <div class="p-4">
        <header class="mb-3 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <h3 class="text-heading-3 text-n-slate-12">
              {{
                scope === 'column' && columnName
                  ? t('PIPELINE.FILTER_ITEMS') + ': ' + columnName
                  : t('PIPELINE.FILTER_ITEMS')
              }}
            </h3>
            <div
              v-if="hasActiveFilters && filteredResults.total > 0"
              class="flex gap-1"
            >
              <div
                class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-3 text-n-slate-12 whitespace-nowrap gap-1"
              >
                {{ filteredResults.total }} {{ t('PIPELINE.FILTER.RESULTS') }}
              </div>
              <div
                v-for="(count, stageName) in filteredResults.stages"
                :key="stageName"
                class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full text-white whitespace-nowrap gap-1"
                :style="{ backgroundColor: getStageColor(stageName) }"
              >
                <span class="text-[10px]">{{ stageName }}</span>
                <span
                  class="bg-white/20 px-1.5 rounded-full text-[10px] min-w-[18px] text-center"
                  >{{ count }}</span
                >
              </div>
            </div>
          </div>
          <button
            variant="clear"
            size="small"
            color-scheme="secondary"
            @click="$emit('close')"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </header>

        <!-- Quick Filters (B4 - Advanced Filters) -->
        <div class="mb-4">
          <label class="text-label text-n-slate-11 mb-2 block">
            {{ t('PIPELINE.QUICK_FILTERS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="qf in quickFilters"
              :key="qf.id"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150 ease-in-out"
              :class="
                isQuickFilterActive(qf.id)
                  ? 'bg-n-alpha-2 border-n-brand text-n-brand'
                  : 'border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1 hover:border-n-slate-7'
              "
              @click="applyQuickFilter(qf)"
            >
              <fluent-icon :icon="qf.icon" size="14" />
              <span>{{ t(qf.label) }}</span>
            </button>
          </div>
        </div>

        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-hidden"
        >
          <!-- Filter Controls -->
          <div class="space-y-2 border-r border-n-weak md:pr-4 overflow-y-auto">
            <!-- NOOVI: Filtro por Label (etiqueta da conversa vinculada).
                 Só no escopo global do board — o filtro por-coluna é
                 client-side e não trata labels. -->
            <div
              v-if="scope !== 'column' && labelOptions.length > 0"
              class="p-2 bg-n-solid-2 rounded-lg"
            >
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FILTER.LABELS.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="label in labelOptions"
                  :key="label.id"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-all duration-150 ease-in-out"
                  :class="
                    filters.labels.includes(label.title)
                      ? 'font-semibold border-n-slate-7 bg-n-alpha-2 text-n-slate-12'
                      : 'font-medium border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1'
                  "
                  @click="toggleLabel(label.title)"
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: label.color }"
                  />
                  {{ label.title }}
                </button>
              </div>
            </div>

            <!-- Stage Filter (B4 - Advanced Filters) -->
            <div
              v-if="availableStages.length > 0"
              class="p-2 bg-n-solid-2 rounded-lg"
            >
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.STAGE.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="stage in availableStages"
                  :key="stage.id"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-all duration-150 ease-in-out"
                  :class="
                    filters.stages.includes(stage.id)
                      ? 'font-semibold'
                      : 'font-medium border-n-weak text-n-slate-11 bg-n-solid-2 hover:bg-n-alpha-1'
                  "
                  :style="{
                    borderColor: filters.stages.includes(stage.id)
                      ? stage.color
                      : undefined,
                    backgroundColor: filters.stages.includes(stage.id)
                      ? `${stage.color}15`
                      : undefined,
                  }"
                  @click="
                    filters.stages = filters.stages.includes(stage.id)
                      ? filters.stages.filter(s => s !== stage.id)
                      : [...filters.stages, stage.id]
                  "
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: stage.color }"
                  />
                  {{ stage.name }}
                </button>
              </div>
            </div>

            <!-- Prioridade -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FORM.PRIORITY.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="option in priorityOptions"
                  :key="option.value"
                  class="border px-3 py-1 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-1.5"
                  :class="[
                    filters.priority.includes(option.value)
                      ? option.colors.selected
                      : [option.colors.border, option.colors.hover],
                    option.colors.text,
                  ]"
                  @click="
                    filters.priority = filters.priority.includes(option.value)
                      ? filters.priority.filter(p => p !== option.value)
                      : [...filters.priority, option.value]
                  "
                >
                  <span
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: getPriorityColor(option.value) }"
                  />
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Status Filter -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="text-label text-n-slate-12 mb-1 block">
                {{ t('PIPELINE.FILTERS.STATUS.LABEL') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  class="border px-3 py-1 text-sm font-medium rounded-lg transition-colors"
                  :class="[
                    filters.status === null
                      ? 'bg-n-alpha-2 border-n-slate-7 text-n-slate-12'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = null"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.ALL') }}
                </button>
                <button
                  class="border px-3 py-1 text-sm font-medium rounded-lg transition-colors"
                  :class="[
                    filters.status === 'open'
                      ? 'bg-n-blue-3 border-n-blue-9 text-n-blue-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'open'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.OPEN') }}
                </button>
                <button
                  class="border px-3 py-1 text-sm font-medium rounded-lg transition-colors"
                  :class="[
                    filters.status === 'won'
                      ? 'bg-n-teal-3 border-n-teal-9 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'won'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.WON') }}
                </button>
                <button
                  class="border px-3 py-1 text-sm font-medium rounded-lg transition-colors"
                  :class="[
                    filters.status === 'lost'
                      ? 'bg-n-ruby-3 border-n-ruby-9 text-n-ruby-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
                  ]"
                  @click="filters.status = 'lost'"
                >
                  {{ t('PIPELINE.FILTERS.STATUS.LOST') }}
                </button>
              </div>
            </div>

            <!-- Fase 2.1 — Filtro SLA excedido -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  :id="fieldId('sla-exceeded')"
                  v-model="filters.sla_exceeded"
                  name="sla_exceeded"
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 rounded border-n-weak text-red-600 focus:ring-red-500 cursor-pointer"
                />
                <div class="flex-1">
                  <span class="text-label text-n-slate-12 block">
                    <fluent-icon
                      icon="warning"
                      size="14"
                      class="inline-block text-red-500"
                    />
                    {{ t('PIPELINE.SLA.FILTER_LABEL') }}
                  </span>
                  <span class="text-xs text-n-slate-10 block mt-1">
                    {{ t('PIPELINE.SLA.FILTER_HELP') }}
                  </span>
                </div>
              </label>
            </div>

            <!-- Valor -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('value-min')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.VALUE.LABEL') }}
              </label>
              <div class="flex gap-2">
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('value-min')"
                    v-model="filters.value.min"
                    name="value_min"
                    type="number"
                    :placeholder="t('PIPELINE.VALUE.MIN')"
                    :aria-label="`${t('PIPELINE.VALUE.LABEL')}: ${t('PIPELINE.VALUE.MIN')}`"
                  />
                </div>
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('value-max')"
                    v-model="filters.value.max"
                    name="value_max"
                    type="number"
                    :placeholder="t('PIPELINE.VALUE.MAX')"
                    :aria-label="`${t('PIPELINE.VALUE.LABEL')}: ${t('PIPELINE.VALUE.MAX')}`"
                  />
                </div>
              </div>
            </div>

            <!-- Agente Responsável -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('agent')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.AGENT.LABEL') }}
              </label>
              <div class="relative">
                <select
                  :id="fieldId('agent')"
                  v-model="filters.agent_id"
                  name="agent_id"
                  class="h-10 w-full !mb-0 appearance-none bg-none rounded-lg border-0 bg-n-surface-1 py-2 pl-3 pr-10 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 transition-all duration-200 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9"
                >
                  <option :value="null">
                    {{ t('PIPELINE.FILTER.AGENT.ALL') }}
                  </option>
                  <option :value="-1">
                    {{ t('PIPELINE.FILTER.AGENT.NONE') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <fluent-icon
                    icon="chevron-down"
                    size="16"
                    class="text-n-slate-11"
                  />
                </div>
              </div>
            </div>

            <!-- Data de Criação -->
            <div class="p-2 bg-n-solid-2 rounded-lg">
              <label
                :for="fieldId('date-start')"
                class="text-label text-n-slate-12 mb-1 block"
              >
                {{ t('PIPELINE.FILTER.DATE.LABEL') }}
              </label>
              <div class="flex gap-2">
                <div class="flex-1 date-picker no-margin">
                  <DatePicker
                    v-model:value="filters.date.start"
                    type="date"
                    format="DD/MM/YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="t('PIPELINE.FILTER.DATE.START')"
                    :input-attr="{
                      id: fieldId('date-start'),
                      name: 'date_start',
                      'aria-label': `${t('PIPELINE.FILTER.DATE.LABEL')}: ${t('PIPELINE.FILTER.DATE.START')}`,
                    }"
                  />
                </div>
                <div class="flex-1 date-picker no-margin">
                  <DatePicker
                    v-model:value="filters.date.end"
                    type="date"
                    format="DD/MM/YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="t('PIPELINE.FILTER.DATE.END')"
                    :input-attr="{
                      id: fieldId('date-end'),
                      name: 'date_end',
                      'aria-label': `${t('PIPELINE.FILTER.DATE.LABEL')}: ${t('PIPELINE.FILTER.DATE.END')}`,
                    }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Preset Management -->
          <div class="space-y-3 overflow-y-auto">
            <h4
              class="text-heading-3 text-n-slate-12 border-b border-n-weak pb-1 mb-2"
            >
              {{ t('PIPELINE.PRESET.TITLE') }}
            </h4>

            <!-- Save Current -->
            <div class="space-y-1">
              <label
                :for="fieldId('preset-name')"
                class="text-label text-n-slate-12 block"
              >
                {{ t('PIPELINE.PRESET.SAVE_CURRENT') }}
              </label>
              <div class="flex gap-1">
                <div class="flex-1">
                  <FormInput
                    :id="fieldId('preset-name')"
                    v-model="newPresetName"
                    name="preset_name"
                    type="text"
                    :placeholder="t('PIPELINE.PRESET.NAME_PLACEHOLDER')"
                    @keyup.enter="saveCurrentPreset"
                  />
                </div>
                <button
                  variant="smooth"
                  size="small"
                  icon="save"
                  color-scheme="secondary"
                  :disabled="!newPresetName.trim()"
                  @click="saveCurrentPreset"
                />
              </div>
            </div>

            <!-- Saved Presets List -->
            <div class="space-y-1">
              <label class="text-label text-n-slate-12 block">
                {{ t('PIPELINE.PRESET.SAVED_PRESETS') }}
              </label>
              <div
                v-if="Object.keys(savedPresets).length === 0"
                class="text-xs text-n-slate-10 p-2 text-center"
              >
                {{ t('PIPELINE.PRESET.NO_PRESETS') }}
              </div>
              <ul v-else class="max-h-48 overflow-y-auto space-y-1 pr-1">
                <li
                  v-for="(presetFilters, name) in savedPresets"
                  :key="name"
                  class="flex justify-between items-center p-1.5 rounded-md flex-wrap"
                  :class="{
                    'bg-n-alpha-2': appliedPresetName === name,
                    'hover:bg-n-alpha-1': appliedPresetName !== name,
                  }"
                >
                  <div class="flex-1 min-w-0 mr-2">
                    <span
                      class="text-sm font-medium text-n-slate-12 block truncate"
                      :title="name"
                      >{{ name }}</span
                    >
                    <span
                      class="text-xs text-n-slate-10 block truncate"
                      :title="getPresetSummary(presetFilters)"
                    >
                      {{ getPresetSummary(presetFilters) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <button
                      variant="clear"
                      size="small"
                      color-scheme="secondary"
                      :title="t('PIPELINE.PRESET.APPLY')"
                      @click="applyPreset(name)"
                    >
                      <fluent-icon icon="play-circle" size="14" />
                    </button>
                    <button
                      variant="clear"
                      size="small"
                      color-scheme="alert"
                      :title="t('PIPELINE.PRESET.DELETE')"
                      @click="deletePreset(name)"
                    >
                      <fluent-icon icon="delete" size="14" />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <footer
          class="flex justify-end space-x-2 pt-3 mt-3 border-t border-n-weak"
        >
          <button
            class="px-4 py-2 text-n-slate-11 hover:text-n-slate-12"
            @click="handleClear"
          >
            {{ t('PIPELINE.FORM.CLEAR') }}
          </button>
          <button
            class="px-4 py-2 bg-n-brand text-white rounded-lg hover:bg-n-brand/90"
            @click="handleApply"
          >
            {{ t('PIPELINE.FORM.APPLY') }}
          </button>
        </footer>
      </div>
    </Modal>

    <Dialog
      ref="overwriteDialogRef"
      type="edit"
      :title="t('PIPELINE.PRESET.OVERWRITE_TITLE')"
      :description="
        t('PIPELINE.PRESET.CONFIRM_OVERWRITE', { name: pendingPresetName })
      "
      :confirm-button-label="t('PIPELINE.PRESET.OVERWRITE_CONFIRM')"
      @confirm="confirmOverwritePreset"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('PIPELINE.PRESET.DELETE_TITLE')"
      :description="
        t('PIPELINE.PRESET.CONFIRM_DELETE', { name: pendingPresetName })
      "
      :confirm-button-label="t('PIPELINE.PRESET.DELETE')"
      @confirm="confirmDeletePreset"
    />
  </Teleport>
</template>

<style scoped>
/* Custom scrollbar for preset list */
ul {
  scrollbar-width: thin;
  scrollbar-color: var(--n-brand) var(--n-alpha-1);
}

ul::-webkit-scrollbar {
  width: 6px;
}

ul::-webkit-scrollbar-track {
  background: var(--n-alpha-1);
  border-radius: 3px;
}

ul::-webkit-scrollbar-thumb {
  background-color: var(--n-brand);
  border-radius: 3px;
}
</style>
